import { useState, useRef, useEffect, useCallback } from 'react';
import {
  X, Brush, Type, Eraser, Undo2, Trash2, Loader2, Send,
  Square, ArrowUpRight, Circle as CircleIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Stage, Layer, Line, Rect, Ellipse, Arrow, Text as KText, Image as KImage } from 'react-konva';
import axios from 'axios';
import { HudButton } from '../hud';
import { useReserveVRAM } from '../../lib/MapVRAMGuard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/**
 * AnnotationDialog — Sprint D3 (Feb 2026).
 *
 * Modal that lets the user :
 *  - draw freehand strokes, rectangles, ellipses, arrows
 *  - add short text labels
 *  - pick a color (5-color palette)
 *  - undo last shape / clear all
 *  - type a revision instruction (sticky bottom)
 *
 * On submit, the dialog produces TWO PNGs from the Konva stage :
 *  - overlay : the annotations alone (transparent background)
 *  - composed : the original image + annotations flattened
 * and POSTs them along with the textual prompt to
 * `/projects/{id}/generations/{job_id}/revise`.
 */
const TOOLS = [
  { id: 'brush', icon: Brush, label: 'Pinceau' },
  { id: 'rect',  icon: Square, label: 'Rectangle' },
  { id: 'ellipse', icon: CircleIcon, label: 'Ellipse' },
  { id: 'arrow', icon: ArrowUpRight, label: 'Flèche' },
  { id: 'text',  icon: Type, label: 'Texte' },
  { id: 'eraser', icon: Eraser, label: 'Gomme' },
];

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#FFFFFF'];

const AnnotationDialog = ({
  open,
  onClose,
  projectId,
  generation,         // { job_id, image_url, lot_number, ... }
  onSubmitted,        // (newJobId) => void
}) => {
  const [tool, setTool] = useState('brush');
  const [color, setColor] = useState(COLORS[0]);
  const [stroke, setStroke] = useState(4);
  const [shapes, setShapes] = useState([]);
  const [drafting, setDrafting] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bgImage, setBgImage] = useState(null);
  const [stageSize, setStageSize] = useState({ w: 800, h: 600, scale: 1 });
  const stageRef = useRef(null);
  const containerRef = useRef(null);

  // Reserve VRAM while this Konva canvas is open so the Hub3DMap (Photoreal)
  // auto-downgrades to Vector and prevents Chrome from triggering
  // CONTEXT_LOST_WEBGL on heavy multi-layer scenes.
  useReserveVRAM(open ? 'annotation-dialog' : null);

  // Load the background image.
  // Important: we want the cookie to be sent (cross-origin auth) AND the
  // canvas to remain exportable via toDataURL(). The only HTML construct that
  // satisfies BOTH is `crossOrigin="use-credentials"` — the browser sends the
  // session cookie and verifies the CORS response carries
  // `Access-Control-Allow-Credentials: true` (which our backend does). With
  // `anonymous` the cookie is dropped → 401 from the auth-protected endpoint.
  useEffect(() => {
    if (!open || !generation?.image_url) return;
    setShapes([]);
    setPrompt('');
    setBgImage(null);

    let cancelled = false;
    const url = generation.image_url?.startsWith('http')
      ? generation.image_url
      : `${process.env.REACT_APP_BACKEND_URL}${generation.image_url || ''}`;

    const img = new window.Image();
    img.crossOrigin = 'use-credentials';
    img.onload = () => { if (!cancelled) setBgImage(img); };
    img.onerror = () => {
      if (cancelled) return;
      // Fallback: try once more without crossOrigin (canvas will be tainted
      // but the user at least sees the image). The submit handler tolerates
      // a tainted canvas by submitting only the text prompt.
      const img2 = new window.Image();
      img2.onload = () => { if (!cancelled) setBgImage(img2); };
      img2.onerror = () => { if (!cancelled) toast.error('Impossible de charger l\'image à annoter'); };
      img2.src = url;
    };
    img.src = url;

    return () => { cancelled = true; };
  }, [open, generation?.image_url]);

  // Fit stage to container while preserving aspect ratio
  useEffect(() => {
    if (!bgImage) return;
    const fit = () => {
      const c = containerRef.current;
      if (!c) return;
      const maxW = c.clientWidth;
      const maxH = c.clientHeight;
      const ratio = bgImage.width / bgImage.height;
      let w = maxW, h = maxW / ratio;
      if (h > maxH) { h = maxH; w = maxH * ratio; }
      setStageSize({ w, h, scale: w / bgImage.width });
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [bgImage]);

  // Drawing handlers ----------------------------------------------------------

  const getPos = (e) => {
    const stage = e.target.getStage();
    const p = stage.getPointerPosition();
    if (!p) return null;
    // Normalize coords to ORIGINAL image space (so we can scale safely)
    return { x: p.x / stageSize.scale, y: p.y / stageSize.scale };
  };

  const handleMouseDown = (e) => {
    if (!bgImage) return;
    const pos = getPos(e);
    if (!pos) return;
    if (tool === 'text') {
      const t = window.prompt('Texte à insérer :', '');
      if (t && t.trim()) {
        setShapes((s) => [...s, {
          id: Date.now(), tool: 'text', x: pos.x, y: pos.y, text: t.trim(),
          color, fontSize: 22,
        }]);
      }
      return;
    }
    if (tool === 'brush' || tool === 'eraser') {
      setDrafting({
        id: Date.now(), tool: 'line', points: [pos.x, pos.y], color, stroke,
        composite: tool === 'eraser' ? 'destination-out' : 'source-over',
      });
    } else if (tool === 'rect') {
      setDrafting({ id: Date.now(), tool: 'rect', x: pos.x, y: pos.y, w: 0, h: 0, color, stroke });
    } else if (tool === 'ellipse') {
      setDrafting({ id: Date.now(), tool: 'ellipse', cx: pos.x, cy: pos.y, rx: 0, ry: 0, color, stroke });
    } else if (tool === 'arrow') {
      setDrafting({ id: Date.now(), tool: 'arrow', x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y, color, stroke });
    }
  };

  const handleMouseMove = (e) => {
    if (!drafting) return;
    const pos = getPos(e);
    if (!pos) return;
    setDrafting((d) => {
      if (!d) return d;
      if (d.tool === 'line') return { ...d, points: [...d.points, pos.x, pos.y] };
      if (d.tool === 'rect') return { ...d, w: pos.x - d.x, h: pos.y - d.y };
      if (d.tool === 'ellipse') return { ...d, rx: Math.abs(pos.x - d.cx), ry: Math.abs(pos.y - d.cy) };
      if (d.tool === 'arrow') return { ...d, x2: pos.x, y2: pos.y };
      return d;
    });
  };

  const handleMouseUp = () => {
    if (drafting) {
      setShapes((s) => [...s, drafting]);
      setDrafting(null);
    }
  };

  // Undo / clear -------------------------------------------------------------

  const undo = useCallback(() => setShapes((s) => s.slice(0, -1)), []);
  const clearAll = useCallback(() => setShapes([]), []);

  // Submit -------------------------------------------------------------------

  const submit = async () => {
    if (!prompt.trim()) {
      toast.error('Décrivez ce qu\'il faut modifier');
      return;
    }
    if (shapes.length === 0 && prompt.trim().length < 12) {
      toast.error('Ajoutez au moins une annotation OU un commentaire détaillé');
      return;
    }
    setSubmitting(true);
    try {
      // Produce overlay PNG (annotations only, transparent bg)
      const stage = stageRef.current;
      if (!stage) throw new Error('Canvas indisponible');

      // Hide background layer to export overlay alone
      const bgLayer = stage.findOne('.bg-layer');
      const annLayer = stage.findOne('.annotation-layer');
      let overlayDataUrl = null;
      let composedDataUrl = null;

      try {
        if (bgLayer) bgLayer.hide();
        overlayDataUrl = stage.toDataURL({
          pixelRatio: bgImage.width / stageSize.w,
          mimeType: 'image/png',
        });
        if (bgLayer) bgLayer.show();
        composedDataUrl = stage.toDataURL({
          pixelRatio: bgImage.width / stageSize.w,
          mimeType: 'image/png',
        });
      } catch (canvasErr) {
        // Canvas tainted (CORS image couldn't be marked CORS-clean). Fall
        // back to overlay-only export (no bg image) ; backend will then
        // re-compose using the original stored image.
        if (bgLayer) bgLayer.show();
        if (bgLayer) bgLayer.hide();
        overlayDataUrl = stage.toDataURL({
          pixelRatio: 2,
          mimeType: 'image/png',
        });
        if (bgLayer) bgLayer.show();
        composedDataUrl = null; // backend will flatten server-side
        toast.info('Canvas en mode dégradé — la composition se fera côté serveur');
      }
      annLayer?.show();

      const r = await axios.post(
        `${API}/projects/${projectId}/generations/${generation.job_id}/revise`,
        {
          annotated_overlay_b64: overlayDataUrl,
          composed_image_b64: composedDataUrl,
          revision_prompt: prompt.trim(),
        },
        opts,
      );
      toast.success('Révision lancée — le rendu corrigé apparaîtra dans quelques instants');
      onSubmitted?.(r.data?.new_job_id);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la révision');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)' }}
      data-testid="annotation-dialog"
    >
      <div
        className="rounded-2xl flex flex-col"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          boxShadow: '0 24px 64px -16px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Sprint D3</span>
            <h2
              className="font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Annoter & corriger le rendu
            </h2>
            <p className="text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              {generation?.lot_number || generation?.job_id} — dessinez les zones à modifier, l'IA préserve le reste à l'identique.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
            data-testid="annotation-close-btn"
          >
            <X className="w-4 h-4" style={{ color: 'var(--c-text)' }} />
          </button>
        </div>

        {/* Toolbar */}
        <div
          className="flex items-center gap-1.5 px-5 py-2 flex-wrap"
          style={{ borderBottom: '1px solid var(--c-border)' }}
          data-testid="annotation-toolbar"
        >
          <div className="flex items-center gap-1 mr-3">
            {TOOLS.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setTool(id)}
                title={label}
                data-testid={`annotation-tool-${id}`}
                className="p-1.5 rounded-md transition-colors"
                style={{
                  background: tool === id ? 'var(--c-gold-soft)' : 'var(--c-surface-2)',
                  border: `1px solid ${tool === id ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                  color: tool === id ? 'var(--c-gold)' : 'var(--c-text-2)',
                }}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 mr-3">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                title={c}
                data-testid={`annotation-color-${c.slice(1)}`}
                className="w-6 h-6 rounded-full transition-transform"
                style={{
                  background: c,
                  border: color === c ? '2px solid var(--c-gold)' : '2px solid var(--c-border)',
                  transform: color === c ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: color === c ? `0 0 8px ${c}80` : 'none',
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: 'var(--c-text-2)' }}>
            <span>Épaisseur</span>
            <input
              type="range"
              min={1}
              max={20}
              value={stroke}
              onChange={(e) => setStroke(Number(e.target.value))}
              className="w-20"
              data-testid="annotation-stroke-slider"
            />
            <span className="tabular-nums w-4">{stroke}</span>
          </div>
          <div className="flex-1" />
          <button
            onClick={undo}
            disabled={shapes.length === 0}
            title="Annuler"
            className="p-1.5 rounded-md disabled:opacity-30"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
            data-testid="annotation-undo-btn"
          >
            <Undo2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={clearAll}
            disabled={shapes.length === 0}
            title="Tout effacer"
            className="p-1.5 rounded-md disabled:opacity-30"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: '#FCA5A5' }}
            data-testid="annotation-clear-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Canvas area */}
        <div
          ref={containerRef}
          className="flex-1 min-h-0 flex items-center justify-center p-4"
          style={{ background: 'var(--c-surface-2)' }}
          data-testid="annotation-canvas-wrap"
        >
          {!bgImage ? (
            <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--c-text-2)' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de l'image…
            </div>
          ) : (
            <Stage
              ref={stageRef}
              width={stageSize.w}
              height={stageSize.h}
              scale={{ x: stageSize.scale, y: stageSize.scale }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              style={{
                borderRadius: 8,
                cursor: tool === 'text' ? 'text' : 'crosshair',
                boxShadow: '0 0 0 1px var(--c-border)',
                background: '#000',
              }}
            >
              <Layer name="bg-layer" listening={false}>
                <KImage image={bgImage} />
              </Layer>
              <Layer name="annotation-layer">
                {shapes.map((s) => renderShape(s))}
                {drafting && renderShape(drafting)}
              </Layer>
            </Stage>
          )}
        </div>

        {/* Footer — prompt + submit */}
        <div className="px-5 py-3 space-y-2" style={{ borderTop: '1px solid var(--c-border)' }}>
          <label className="hud-eyebrow block">Instruction de révision</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            placeholder="Ex. Changer le parquet du séjour pour un sol foncé en noyer. Garder tout le reste à l'identique."
            data-testid="annotation-prompt-input"
            className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-y"
            style={{
              background: 'var(--c-surface-2)',
              border: '1px solid var(--c-border)',
              color: 'var(--c-text)',
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
              {shapes.length} annotation{shapes.length > 1 ? 's' : ''} · le rendu original est utilisé comme référence stricte pour la préservation
            </p>
            <div className="flex items-center gap-2">
              <HudButton variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
                Annuler
              </HudButton>
              <HudButton
                variant="primary"
                size="sm"
                onClick={submit}
                disabled={submitting || !bgImage}
                leftIcon={submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                data-testid="annotation-submit-btn"
              >
                {submitting ? 'Envoi…' : 'Lancer la révision IA'}
              </HudButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderShape = (s) => {
  const stroke = s.stroke || 4;
  if (s.tool === 'line') {
    return (
      <Line
        key={s.id}
        points={s.points}
        stroke={s.color}
        strokeWidth={stroke}
        tension={0.3}
        lineCap="round"
        lineJoin="round"
        globalCompositeOperation={s.composite || 'source-over'}
      />
    );
  }
  if (s.tool === 'rect') {
    return <Rect key={s.id} x={s.x} y={s.y} width={s.w} height={s.h} stroke={s.color} strokeWidth={stroke} fill="transparent" />;
  }
  if (s.tool === 'ellipse') {
    return <Ellipse key={s.id} x={s.cx} y={s.cy} radiusX={s.rx} radiusY={s.ry} stroke={s.color} strokeWidth={stroke} fill="transparent" />;
  }
  if (s.tool === 'arrow') {
    return (
      <Arrow
        key={s.id}
        points={[s.x1, s.y1, s.x2, s.y2]}
        stroke={s.color}
        fill={s.color}
        strokeWidth={stroke}
        pointerLength={14}
        pointerWidth={14}
      />
    );
  }
  if (s.tool === 'text') {
    return <KText key={s.id} x={s.x} y={s.y} text={s.text} fontSize={s.fontSize || 22} fontStyle="bold" fill={s.color} shadowColor="rgba(0,0,0,0.6)" shadowBlur={4} />;
  }
  return null;
};

export default AnnotationDialog;
